// src/lib/posts/pinSection.ts
//
// Landing on a #type-* section of the metro browse page, read side only.
//
// The four section cards live inside a <details> that is server-rendered
// folded (crawl equity), so nothing built into the browser or Next can reach
// them on a client-side arrival. Next's scroll handler skips zero-rect
// targets, and when loading.tsx shows it has already scrolled the skeleton
// into view and spent its one-shot scroll intent before this page exists.
// Content above the cards also keeps growing for a second or more after
// mount (the thin-metro fill, the category re-fetch, the admin and email
// banners, the font swap on a hard load), so a single scroll computed once
// is displaced by whatever lands after it. Measured live on the shipped
// build, three of five arrivals stayed at scrollY 0 with the section 7,000
// px below.
//
// So this module does three things and nothing else: reveal the fold, land
// instantly, then hold the section at the top for a bounded window until the
// user takes over. It never navigates, never touches the URL or history, and
// never throws.

import { POST_SECTIONS } from './sections';

export const ARRIVAL_STORAGE_KEY = 'js_posts_bucket';

const KNOWN_SECTION_IDS: ReadonlySet<string> = new Set(
  POST_SECTIONS.map((s) => `type-${s.anchor}`),
);

/** True only for an id one of the section cards actually carries. */
export function isSectionId(id: string | null | undefined): id is string {
  return typeof id === 'string' && KNOWN_SECTION_IDS.has(id);
}

export type ArrivalStorage = Pick<Storage, 'getItem' | 'removeItem'>;

/**
 * Which section the visitor is arriving for, or null.
 *
 * The URL hash wins when it names a known section (direct links, hard loads).
 * Otherwise the sessionStorage relay written by /posts is used; it survives
 * the LocationModal detour and the one router path that strips a hash from
 * the landed URL (a redirected RSC fetch). The relay key is always consumed
 * so a later plain visit to a metro page does not jump.
 */
export function readArrivalSection(
  hash: string | null | undefined,
  storage: ArrivalStorage | null | undefined,
): string | null {
  const fromHash = String(hash ?? '').replace(/^#/, '');
  let relayed = '';
  if (storage) {
    try { relayed = storage.getItem(ARRIVAL_STORAGE_KEY) || ''; } catch {}
    try { storage.removeItem(ARRIVAL_STORAGE_KEY); } catch {}
  }
  if (isSectionId(fromHash)) return fromHash;
  if (isSectionId(relayed)) return relayed;
  return null;
}

/**
 * Opens every closed <details> above `el`. Imperative on purpose: `open` is
 * not a React prop on this page, so a later re-render never folds it back.
 */
export function revealSection(el: Element | null | undefined): void {
  let d: HTMLDetailsElement | null = el?.closest('details') ?? null;
  while (d) {
    if (!d.open) d.open = true;
    d = d.parentElement?.closest('details') ?? null;
  }
}

export interface PinSectionOptions {
  /**
   * How long to keep the section pinned. The slowest legitimate shift above
   * the target, the thin-metro fill, lands within about 1.5 s; 3 s covers a
   * slow network. Default 3000.
   */
  holdMs?: number;
}

const DEFAULT_HOLD_MS = 3000;

/** Drift at or below this many pixels is left alone. */
const TOLERANCE_PX = 1;

/**
 * Only for engines without scrollIntoView, where the first landing has no
 * browser-measured position to aim at. Matches scroll-mt-20 on the cards.
 */
const FALLBACK_SCROLL_MARGIN_PX = 80;

/** Inputs that mean a person is now driving the page. */
const STOP_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * A scroll event alone does not mean a person moved the page, because every
 * programmatic scroll fires one too. So the hold also watches where the page
 * actually is: it remembers the offset the browser left after each of its own
 * scrolls, and treats a scroll that ends anywhere else as somebody scrolling.
 * That covers the ways to move a page that fire none of the four inputs above,
 * such as dragging the scrollbar in Firefox, the browser find bar, and the
 * scroll restoration that runs when this page is remounted by a Back.
 */
const SCROLL_OWNERSHIP_PX = 2;

/**
 * Reveal `sectionId`, scroll it to the top instantly, and keep it there for a
 * bounded window. Returns a cancel function; calling it more than once is
 * harmless. Safe to call during SSR (returns a no-op).
 *
 * The hold compares each frame's top against where the browser left the card
 * right after the last scroll, not against a fixed offset. That way the
 * browser decides what "start" means (scroll-mt-20, or as far up as the page
 * can go when the card sits near the bottom), and a card that cannot reach
 * the top is pinned where it landed instead of being scrolled every frame.
 */
export function pinSection(sectionId: string, options: PinSectionOptions = {}): () => void {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    typeof window.requestAnimationFrame !== 'function' ||
    !sectionId
  ) {
    return () => {};
  }

  const holdMs = options.holdMs ?? DEFAULT_HOLD_MS;

  let done = false;
  let frame = 0;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let pinnedTop: number | null = null;
  let ownScrollY: number | null = null;

  const readScrollY = (): number | null => {
    try { return window.scrollY; } catch { return null; }
  };

  /** A scroll that did not end where this module last left the page is a
   *  person scrolling, so the hold gives way to it. */
  const onScroll = (): void => {
    if (done) return;
    const y = readScrollY();
    if (y === null || ownScrollY === null) return;
    if (Math.abs(y - ownScrollY) <= SCROLL_OWNERSHIP_PX) return;
    stop();
  };

  const stop = (): void => {
    if (done) return;
    done = true;
    try { if (frame) window.cancelAnimationFrame(frame); } catch {}
    frame = 0;
    try { if (deadline !== undefined) clearTimeout(deadline); } catch {}
    deadline = undefined;
    for (const ev of STOP_EVENTS) {
      try { window.removeEventListener(ev, stop, { capture: true }); } catch {}
    }
    try { window.removeEventListener('scroll', onScroll, { capture: true }); } catch {}
  };

  // Instant, and it honors scroll-margin-top. Forcing the root's
  // scroll-behavior to auto for the call is what Next's own handler does, so
  // a future `scroll-behavior: smooth` in CSS cannot turn this into an
  // animation that the next layout shift interrupts.
  const scrollInstantly = (el: Element, delta: number): void => {
    let html: HTMLElement | null = null;
    let prior = '';
    try {
      html = document.documentElement;
      prior = html.style.scrollBehavior || '';
      html.style.scrollBehavior = 'auto';
    } catch { html = null; }
    try {
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
      else window.scrollBy(0, delta);
    } catch {
      try { window.scrollBy(0, delta); } catch {}
    } finally {
      try { if (html) html.style.scrollBehavior = prior; } catch {}
    }
  };

  const check = (): void => {
    let el: HTMLElement | null = null;
    try { el = document.getElementById(sectionId); } catch { return; }
    if (!el) return; // not mounted yet; the frame loop keeps looking until the deadline

    try { revealSection(el); } catch {}

    let top: number;
    try { top = el.getBoundingClientRect().top; } catch { return; }
    if (pinnedTop !== null && Math.abs(top - pinnedTop) <= TOLERANCE_PX) return;

    scrollInstantly(el, top - (pinnedTop ?? FALLBACK_SCROLL_MARGIN_PX));

    try { pinnedTop = el.getBoundingClientRect().top; } catch { pinnedTop = top; }
    ownScrollY = readScrollY();
  };

  // A rAF callback runs before this frame's style, layout and paint, and
  // getBoundingClientRect forces layout that includes every DOM change since
  // the last frame, so a correction lands in the same frame the shift would
  // have painted. Rescheduling sits outside the checked work so a throwing
  // DOM call cannot end the loop early.
  const tick = (): void => {
    frame = 0;
    if (done) return;
    try { check(); } catch {}
    if (done) return;
    try { frame = window.requestAnimationFrame(tick); } catch { stop(); }
  };

  for (const ev of STOP_EVENTS) {
    try { window.addEventListener(ev, stop, { capture: true, passive: true }); } catch {}
  }
  // Where the page sits before this module has scrolled anything, so a person
  // who scrolls during the very first frames is not overridden either.
  ownScrollY = readScrollY();
  try { window.addEventListener('scroll', onScroll, { capture: true, passive: true }); } catch {}

  // The one bound. rAF pauses in a hidden tab; a timer does not.
  try { deadline = setTimeout(stop, holdMs); } catch {}

  // Land now (inside the layout phase when called from a layout effect, so
  // the section is in place before the first paint), then hold.
  try { check(); } catch {}
  if (!done) {
    try { frame = window.requestAnimationFrame(tick); } catch { stop(); }
  }

  return stop;
}
